import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Loader2,
  Ticket,
  ChevronDown,
  ChevronUp,
  PlusCircle,
  MinusCircle,
  Trash2,
  Calendar,
  Clock,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import {
  usePatientPunchCards,
  usePunchCardUsages,
  usePunchCardAdjustments,
  useDeletePunchCard,
  useCreateAdjustment,
  type PunchCard,
} from '@/hooks/usePatientPunchCards';
import { CreatePunchCardModal } from './CreatePunchCardModal';

interface PunchCardTabProps {
  patientId: string;
  clinicId: string;
}

export function PunchCardTab({ patientId, clinicId }: PunchCardTabProps) {
  const { t } = useTranslation();
  const { data: punchCards, isLoading } = usePatientPunchCards(patientId);
  const deleteMutation = useDeletePunchCard();

  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<PunchCard | null>(null);
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'deduct'>('add');
  const [adjustmentAmount, setAdjustmentAmount] = useState(1);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const toggleCardExpansion = (cardId: string) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  const openAdjustmentModal = (card: PunchCard, type: 'add' | 'deduct') => {
    setSelectedCard(card);
    setAdjustmentType(type);
    setAdjustmentAmount(1);
    setAdjustmentReason('');
    setAdjustmentModalOpen(true);
  };

  const handleDelete = async (card: PunchCard) => {
    if (
      !window.confirm(
        t('patients.punch_cards.confirm_delete', `Are you sure you want to delete this punch card?`),
      )
    ) {
      return;
    }

    try {
      await deleteMutation.mutateAsync({
        punch_card_id: card.id,
        patient_id: patientId,
      });
      toast.success(t('patients.punch_cards.deleted', 'Punch card deleted'));
    } catch (error) {
      toast.error(t('patients.punch_cards.delete_error', 'Failed to delete punch card'));
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('da-DK', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const getStatusBadgeClasses = (status: PunchCard['status']): string => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200';
      case 'expired':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200';
      case 'used':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'cancelled':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusLabel = (status: PunchCard['status']): string => {
    switch (status) {
      case 'active':
        return t('patients.punch_cards.status.active', 'Active');
      case 'expired':
        return t('patients.punch_cards.status.expired', 'Expired');
      case 'used':
        return t('patients.punch_cards.status.used', 'Used Up');
      case 'cancelled':
        return t('patients.punch_cards.status.cancelled', 'Cancelled');
      default:
        return status;
    }
  };

  const remainingClips = (card: PunchCard) => card.total_clips - card.used_clips;

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">
          {t('patients.punch_cards.section_title', 'Punch Cards')}
        </h2>
        <Button size="sm" onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4" />
          {t('patients.punch_cards.new_card', 'New Punch Card')}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-white p-12 dark:border-gray-700 dark:bg-card-dark">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : !punchCards || punchCards.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center dark:border-gray-700 dark:bg-card-dark">
          <Ticket className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            {t('patients.punch_cards.no_cards_title', 'No punch cards yet')}
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {t(
              'punch_cards.no_cards_description',
              'This patient has not been assigned any punch cards yet.',
            )}
          </p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          {punchCards.map((card) => (
            <PunchCardItem
              key={card.id}
              card={card}
              expanded={expandedCards.has(card.id)}
              onToggleExpand={() => toggleCardExpansion(card.id)}
              onAdjust={(type) => openAdjustmentModal(card, type)}
              onDelete={() => handleDelete(card)}
              formatDate={formatDate}
              getStatusBadgeClasses={getStatusBadgeClasses}
              getStatusLabel={getStatusLabel}
              remainingClips={remainingClips(card)}
            />
          ))}
        </div>
      )}

      {adjustmentModalOpen && selectedCard && (
        <AdjustmentModal
          card={selectedCard}
          patientId={patientId}
          type={adjustmentType}
          amount={adjustmentAmount}
          reason={adjustmentReason}
          onAmountChange={setAdjustmentAmount}
          onReasonChange={setAdjustmentReason}
          onClose={() => setAdjustmentModalOpen(false)}
        />
      )}

      {showCreateModal && (
        <CreatePunchCardModal
          patientId={patientId}
          clinicId={clinicId}
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </section>
  );
}

interface PunchCardItemProps {
  card: PunchCard;
  expanded: boolean;
  onToggleExpand: () => void;
  onAdjust: (type: 'add' | 'deduct') => void;
  onDelete: () => void;
  formatDate: (date: string | null) => string;
  getStatusBadgeClasses: (status: PunchCard['status']) => string;
  getStatusLabel: (status: PunchCard['status']) => string;
  remainingClips: number;
}

function PunchCardItem({
  card,
  expanded,
  onToggleExpand,
  onAdjust,
  onDelete,
  formatDate,
  getStatusBadgeClasses,
  getStatusLabel,
  remainingClips,
}: PunchCardItemProps) {
  const { t } = useTranslation();
  const { data: usages } = usePunchCardUsages(expanded ? card.id : null);
  const { data: adjustments } = usePunchCardAdjustments(expanded ? card.id : null);

  const progressPercent = (remainingClips / card.total_clips) * 100;

  const combinedHistory = [
    ...(usages?.map((u) => ({ ...u, type: 'usage' as const })) ?? []),
    ...(adjustments?.map((a) => ({ ...a, type: 'adjustment' as const })) ?? []),
  ].sort((a, b) => {
    const dateA = 'used_at' in a ? a.used_at : a.adjusted_at;
    const dateB = 'used_at' in b ? b.used_at : b.adjusted_at;
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-card-dark">
      <div className="border-b border-gray-200 p-5 dark:border-gray-700">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h4 className="truncate text-lg font-medium text-gray-900 dark:text-white">
              {card.name}
            </h4>
            {(card.punch_card_number || card.invoice_number) && (
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                {card.punch_card_number && (
                  <span className="font-mono text-indigo-700 dark:text-indigo-300">
                    {card.punch_card_number}
                  </span>
                )}
                {card.invoice_number && (
                  <span className="text-gray-500 dark:text-gray-400">
                    {t('patients.punch_cards.invoice', 'Invoice')} {card.invoice_number}
                  </span>
                )}
              </div>
            )}
          </div>
          <span
            className={`inline-flex shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeClasses(card.status)}`}
          >
            {getStatusLabel(card.status)}
          </span>
        </div>
      </div>

      <div className="p-5">
        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {t('patients.punch_cards.usage', 'Usage')}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {remainingClips} / {card.total_clips}{' '}
                {t('patients.punch_cards.clips_remaining', 'clips remaining')}
              </span>
              <div className="ml-2 flex items-center gap-1">
                <button
                  onClick={() => onAdjust('add')}
                  className="rounded p-1 text-green-600 transition-colors hover:bg-green-50 hover:text-green-700 dark:text-green-400 dark:hover:bg-green-900/20 dark:hover:text-green-300"
                  title={t('patients.punch_cards.add_clips', 'Add clips')}
                >
                  <PlusCircle className="h-5 w-5" />
                </button>
                <button
                  onClick={() => onAdjust('deduct')}
                  className="rounded p-1 text-orange-600 transition-colors hover:bg-orange-50 hover:text-orange-700 dark:text-orange-400 dark:hover:bg-orange-900/20 dark:hover:text-orange-300"
                  title={t('patients.punch_cards.deduct_clips', 'Deduct clips')}
                  disabled={remainingClips <= 0}
                >
                  <MinusCircle className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-2.5 rounded-full bg-indigo-600"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              {t('patients.punch_cards.purchased', 'Purchased')}
            </span>
            <span className="block text-sm text-gray-900 dark:text-white">
              {formatDate(card.purchased_at)}
            </span>
          </div>
          <div>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              {t('patients.punch_cards.expires', 'Expires')}
            </span>
            <span className="block text-sm text-gray-900 dark:text-white">
              {formatDate(card.expires_at)}
            </span>
          </div>
        </div>

        <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
          <button
            onClick={onToggleExpand}
            className="flex w-full items-center justify-between text-sm font-medium text-gray-700 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          >
            <span>{t('patients.punch_cards.history', 'Usage History')}</span>
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {expanded && (
            <div className="mt-3 space-y-2">
              {combinedHistory.length === 0 ? (
                <p className="py-2 text-center text-sm text-gray-500 dark:text-gray-400">
                  {t('patients.punch_cards.no_history', 'No usage history yet')}
                </p>
              ) : (
                combinedHistory.map((item) =>
                  item.type === 'usage' ? (
                    <UsageItem key={`usage-${item.id}`} usage={item} />
                  ) : (
                    <AdjustmentItem key={`adjustment-${item.id}`} adjustment={item} />
                  ),
                )
              )}
            </div>
          )}
        </div>

        <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
          <button
            onClick={onDelete}
            className="flex items-center text-sm font-medium text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
          >
            <Trash2 className="mr-1 h-4 w-4" />
            {t('patients.punch_cards.delete', 'Delete punch card')}
          </button>
        </div>
      </div>
    </div>
  );
}

function UsageItem({ usage }: { usage: any }) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-700/50">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {usage.session_name || usage.appointment_type_name || t('patients.punch_cards.treatment', 'Treatment')}
          </p>
          <div className="mt-1 space-y-1">
            {usage.session_date && (
              <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                <Calendar className="mr-1 h-3 w-3" />
                {new Date(usage.session_date).toLocaleDateString('da-DK')}
              </div>
            )}
            {usage.session_time && (
              <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                <Clock className="mr-1 h-3 w-3" />
                {usage.session_time}
              </div>
            )}
            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
              <Ticket className="mr-1 h-3 w-3" />
              {t('patients.punch_cards.used_clips', { count: usage.clips_used })}
            </div>
          </div>
        </div>
        <div className="ml-3 text-right">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('patients.punch_cards.remaining_after', 'Remaining')}
          </p>
          <p className="text-sm font-bold text-gray-900 dark:text-white">
            {usage.remaining_punches_after}
          </p>
        </div>
      </div>
    </div>
  );
}

function AdjustmentItem({ adjustment }: { adjustment: any }) {
  const { t } = useTranslation();
  const isAddition = adjustment.type === 'add';

  return (
    <div
      className={`rounded-lg border p-3 ${
        isAddition
          ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
          : 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-900/20'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {isAddition
              ? t('patients.punch_cards.adjustment_added', '✓ Clips added manually')
              : t('patients.punch_cards.adjustment_deducted', '− Clips deducted manually')}
          </p>
          <div className="mt-1 space-y-1">
            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
              <Calendar className="mr-1 h-3 w-3" />
              {new Date(adjustment.adjusted_at).toLocaleDateString('da-DK', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
              <Ticket className="mr-1 h-3 w-3" />
              {isAddition ? t('patients.punch_cards.added', 'Added') : t('patients.punch_cards.deducted', 'Deducted')}:{' '}
              {adjustment.amount} {t('patients.punch_cards.clips', 'clips')}
            </div>
            {adjustment.adjusted_type_name && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t('patients.punch_cards.type', 'Type')}: {adjustment.adjusted_type_name}
              </div>
            )}
            <div className="mt-2 rounded bg-white p-2 text-xs italic text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              <strong>{t('patients.punch_cards.reason', 'Reason')}:</strong> {adjustment.reason}
            </div>
          </div>
        </div>
        <div className="ml-3 text-right">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('patients.punch_cards.remaining_after', 'Remaining')}
          </p>
          <p className="text-sm font-bold text-gray-900 dark:text-white">{adjustment.new_remaining}</p>
        </div>
      </div>
    </div>
  );
}

interface AdjustmentModalProps {
  card: PunchCard;
  patientId: string;
  type: 'add' | 'deduct';
  amount: number;
  reason: string;
  onAmountChange: (amount: number) => void;
  onReasonChange: (reason: string) => void;
  onClose: () => void;
}

function AdjustmentModal({
  card,
  patientId,
  type,
  amount,
  reason,
  onAmountChange,
  onReasonChange,
  onClose,
}: AdjustmentModalProps) {
  const { t } = useTranslation();
  const createAdjustment = useCreateAdjustment();

  const remainingClips = card.total_clips - card.used_clips;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error(t('patients.punch_cards.reason_required', 'Please provide a reason'));
      return;
    }

    const newRemaining = type === 'add' ? remainingClips + amount : remainingClips - amount;
    const newTotal = type === 'add' ? card.total_clips + amount : card.total_clips;

    try {
      await createAdjustment.mutateAsync({
        punch_card_id: card.id,
        patient_id: patientId,
        type,
        amount,
        reason: reason.trim(),
        previous_remaining: remainingClips,
        new_remaining: Math.max(0, newRemaining),
        previous_total: card.total_clips,
        new_total: newTotal,
      });

      toast.success(
        type === 'add'
          ? t('patients.punch_cards.clips_added', 'Clips added successfully')
          : t('patients.punch_cards.clips_deducted', 'Clips deducted successfully'),
      );
      onClose();
    } catch (error) {
      toast.error(t('patients.punch_cards.adjustment_error', 'Failed to adjust punch card'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4 dark:bg-black/50">
      <div className="flex w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {type === 'add'
              ? t('patients.punch_cards.add_clips_title', 'Add Clips')
              : t('patients.punch_cards.deduct_clips_title', 'Deduct Clips')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <span className="sr-only">{t('common.close', 'Close')}</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-6 p-6">
          <div
            className={`rounded-lg p-4 ${type === 'add' ? 'bg-green-50 dark:bg-green-900/20' : 'bg-orange-50 dark:bg-orange-900/20'}`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`rounded-lg p-2 ${type === 'add' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-orange-100 dark:bg-orange-900/30'}`}
              >
                {type === 'add' ? (
                  <PlusCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                ) : (
                  <MinusCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{card.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('patients.punch_cards.current_balance', 'Current balance')}: {remainingClips} /{' '}
                  {card.total_clips} {t('patients.punch_cards.clips', 'clips')}
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('patients.punch_cards.amount_label', 'Amount')}
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => onAmountChange(Math.max(1, amount - 1))}
                disabled={amount <= 1}
                className="rounded-lg border border-gray-300 p-2 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                <MinusCircle className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
              <input
                type="number"
                min="1"
                max={type === 'deduct' ? remainingClips : 99}
                value={amount}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 1;
                  const max = type === 'deduct' ? remainingClips : 99;
                  onAmountChange(Math.max(1, Math.min(val, max)));
                }}
                className="w-20 rounded-lg border border-gray-300 bg-white px-3 py-2 text-center text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <button
                type="button"
                onClick={() => {
                  const max = type === 'deduct' ? remainingClips : 99;
                  onAmountChange(Math.min(max, amount + 1));
                }}
                disabled={type === 'deduct' && amount >= remainingClips}
                className="rounded-lg border border-gray-300 p-2 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                <PlusCircle className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {t('patients.punch_cards.clips', 'clips')}
              </span>
            </div>
          </div>

          <div
            className={`rounded-lg p-3 ${type === 'add' ? 'bg-green-50 dark:bg-green-900/20' : 'bg-orange-50 dark:bg-orange-900/20'}`}
          >
            <p
              className={`text-sm font-medium ${type === 'add' ? 'text-green-800 dark:text-green-200' : 'text-orange-800 dark:text-orange-200'}`}
            >
              {t('patients.punch_cards.preview', 'Preview')}: {remainingClips} →{' '}
              {type === 'add'
                ? remainingClips + amount
                : Math.max(0, remainingClips - amount)}{' '}
              {t('patients.punch_cards.clips', 'clips')}
            </p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('patients.punch_cards.reason_label', 'Reason')} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder={
                type === 'add'
                  ? t('patients.punch_cards.reason_placeholder_add', 'e.g., Compensation for missed session')
                  : t(
                      'punch_cards.reason_placeholder_deduct',
                      'e.g., Manual correction after system error',
                    )
              }
              rows={3}
              className="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <Button variant="secondary" onClick={onClose} disabled={createAdjustment.isPending}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createAdjustment.isPending || !reason.trim()}
            className={
              type === 'add'
                ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                : 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500'
            }
          >
            {createAdjustment.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.saving', 'Saving...')}
              </>
            ) : type === 'add' ? (
              t('patients.punch_cards.add_clips', 'Add Clips')
            ) : (
              t('patients.punch_cards.deduct_clips', 'Deduct Clips')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
